import { useEffect, useState } from "react";
import { getAllAccessType } from "../Service/AccessTypeService/AccessTypeService";
import { getAllHost } from "../Service/HostService/HostService";
import { getAllParther } from "../Service/PartherService/PartherService";
import { getAllLicenceTypeService } from "../Service/LicenceTypeService/LicenceTypeService";

// Listas auxiliares (catálogos) que se usan para mostrar nombres en tablas y
// fichas. Se piden una sola vez y se reutilizan entre componentes, en lugar de
// pedirlas cada vez que se abre una ficha. Si una lista cambia (p. ej. al
// crear o editar un partner) hay que llamar a invalidateCatalog.

export type CatalogKey = "access" | "host" | "partner" | "licence";

const loaders: Record<CatalogKey, () => Promise<any>> = {
  access: getAllAccessType,
  host: getAllHost,
  partner: getAllParther,
  licence: getAllLicenceTypeService,
};

const cache: Partial<Record<CatalogKey, Promise<any[]>>> = {};

export const getCatalog = (key: CatalogKey): Promise<any[]> => {
  if (!cache[key]) {
    cache[key] = loaders[key]()
      .then((response: any) => response.data ?? [])
      .catch((error: any) => {
        // Si falla, se descarta para volver a intentarlo la próxima vez
        delete cache[key];
        throw error;
      });
  }
  return cache[key]!;
};

export const invalidateCatalog = (key: CatalogKey) => {
  delete cache[key];
};

/** Devuelve los catálogos pedidos; la primera vez los descarga y después salen de la caché. */
export const useCatalogs = <K extends CatalogKey>(keys: K[]) => {
  const [data, setData] = useState<Partial<Record<K, any[]>>>({});
  const [loading, setLoading] = useState(true);
  const keysId = keys.join(",");

  useEffect(() => {
    let active = true;
    setLoading(true);
    Promise.all(keys.map((key) => getCatalog(key).then((list) => [key, list] as const)))
      .then((entries) => {
        if (active) setData(Object.fromEntries(entries) as Partial<Record<K, any[]>>);
      })
      .catch(() => {
        if (active) setData({});
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keysId]);

  return { ...data, loading } as Partial<Record<K, any[]>> & { loading: boolean };
};
